import { EnhancedDriverData } from '../../driverData/types/driverData'
import { createDataKey } from '../../../shared/utils/balanceRegistryUtils'
import { extractDataValue } from '../../../shared/utils/balanceRegistry'
import { StatementColumn, StatementDataType } from '../types'
import { ReportDataType } from '../../../redux/context/reports/types'
import { BudgetDriver } from '../../../types/budgetDriver/BudgetDriver'

/**
 * Parsed formula variable with its value
 */
export interface FormulaVariable {
  name: string // Variable name from formula (e.g., "driver1")
  formulaId: number | null // Matched formula ID
  driverName: string | null // Driver name for display
  value: number | null // Driver value for the period
  budgetDriver: BudgetDriver | null // Budget driver for accessing style and type
}

/**
 * Parsed formula with calculation details
 */
export interface ParsedFormula {
  rawFormula: string
  variables: FormulaVariable[]
  calculatedResult: number | null
  error: string | null
}

/**
 * Extract variable names from formula
 * Matches patterns like: variable('name', ...) or simple variable names like driver1, myVar
 *
 * @param formula - Formula string (e.g., "variable('myydyt-mwh', sum, current) * variable('hinta', average, current)")
 * @returns Array of unique variable names
 */
export const extractVariableNames = (formula: string): string[] => {
  // First try to match variable('name', ...) function calls
  const variableFunctionRegex = /variable\s*\(\s*['"]([^'"]+)['"]/g
  const functionMatches: string[] = []
  let match: RegExpExecArray | null

  // eslint-disable-next-line no-cond-assign
  while ((match = variableFunctionRegex.exec(formula)) !== null) {
    functionMatches.push(match[1]) // Get the first parameter (variable name)
  }

  // If we found variable() function calls, use those
  if (functionMatches.length > 0) {
    return [...new Set(functionMatches)] // Remove duplicates
  }

  // Fallback: Match simple variable names (letters, numbers, underscore)
  // Exclude numbers-only strings
  const simpleVarRegex = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g
  const matches = formula.match(simpleVarRegex) || []

  // Filter out common keywords and get unique names
  const keywords = ['and', 'or', 'not', 'true', 'false', 'null']
  const variables = matches.filter(name => !keywords.includes(name.toLowerCase()))

  return [...new Set(variables)] // Remove duplicates
}

/**
 * Find driver data for a variable name
 * Matches variable names to driver formula.code field (same logic as useNavigateToDriverData)
 *
 * @param variableName - Variable name from formula (e.g., 'myydyt-mwh')
 * @param driverData - All available driver data
 * @param budgetDriverMap - Map of formulaId -> BudgetDriver
 * @param dimensionId - Current dimension ID
 * @returns Matched driver data or null
 */
export const findDriverForVariable = (
  variableName: string,
  driverData: EnhancedDriverData[],
  budgetDriverMap: Map<number, BudgetDriver>,
  dimensionId: string | null
): EnhancedDriverData | null => {
  // Find driver by matching formula.code with variable name
  // This matches the logic in useNavigateToDriverData
  const driver = driverData.find(d => {
    const budgetDriver = budgetDriverMap.get(d.formulaId)
    return budgetDriver?.formula.code === variableName && d.dimensionId === dimensionId
  })

  return driver || null
}

/**
 * Get driver value for specific period
 *
 * @param driver - Driver data
 * @param year - Year
 * @param month - Month (1-12)
 * @returns Driver value or null
 */
export const getDriverValueForPeriod = (driver: EnhancedDriverData, year: number, month: number): number | null => {
  // Create dataKey for the period (format: YYYYMM00)
  const dataKey = createDataKey(year, month)

  // Create a minimal column configuration for extracting data
  const column: StatementColumn = {
    dataSource: 'elements',
    dataKey,
    dataType: StatementDataType.budget,
    columnType: ReportDataType.budget,
    label: '',
    year,
    month
  }

  // Try budget first (DriverData uses 'budget' singular, not 'budgets')
  const budgetValue = extractDataValue(driver, column, 'budget')
  if (budgetValue !== undefined && budgetValue !== null) {
    return budgetValue
  }

  // Fallback to actuals if no budget
  const actualValue = extractDataValue(driver, column, 'actuals')
  if (actualValue !== undefined && actualValue !== null) {
    return actualValue
  }

  return null
}

/**
 * Parse formula and extract variable information
 *
 * Note: This function does NOT calculate the formula result because it requires
 * a proper lexer/parser to safely evaluate complex formulas. It only extracts
 * the driver values used in the formula.
 *
 * @param formula - Formula string
 * @param driverData - All available driver data
 * @param budgetDriverMap - Map of formulaId -> BudgetDriver
 * @param dimensionId - Current dimension ID
 * @param year - Year for values
 * @param month - Month for values
 * @returns Parsed formula with variable details (calculatedResult is always null)
 */
export const parseFormula = (
  formula: string,
  driverData: EnhancedDriverData[],
  budgetDriverMap: Map<number, BudgetDriver>,
  dimensionId: string | null,
  year: number,
  month: number
): ParsedFormula => {
  try {
    const variableNames = extractVariableNames(formula)

    const variables: FormulaVariable[] = variableNames.map(name => {
      const driver = findDriverForVariable(name, driverData, budgetDriverMap, dimensionId)

      if (!driver) {
        return {
          name,
          formulaId: null,
          driverName: null,
          value: null,
          budgetDriver: null
        }
      }

      const value = getDriverValueForPeriod(driver, year, month)
      const budgetDriver = budgetDriverMap.get(driver.formulaId) ?? null

      return {
        name,
        formulaId: driver.formulaId,
        driverName: driver.driverName,
        value,
        budgetDriver
      }
    })

    // Note: We don't calculate the result because it requires a proper lexer/parser
    // to safely evaluate complex formulas. Just show the driver values.
    return {
      rawFormula: formula,
      variables,
      calculatedResult: null,
      error: null
    }
  } catch (error) {
    return {
      rawFormula: formula,
      variables: [],
      calculatedResult: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}
